import { AlertPriority, AlertStatus, ProbableCause } from 'types'
import {
  RolodexEntryIndexType,
  RolodexEntryJson,
} from 'types/rolodex/rolodexEntries'
import {
  parseArray,
  parseBoolean,
  parseFiniteNumber,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

// TODO: Need to have an actual gecko ai agent to get the explanations and causes
// TODO: have an expected range of values for a sensor, maybe this should be on the datapoint entry
export type MonitorLineage = {
  site_id: string
  unit_slug: string
  subsystem_model_artifact_id: string
  subsystem_model_version?: number
  subsystem_partition_artifact_id?: string
  site_context_artifact_id?: string
  generated_module_path?: string
  generator_version: string
}

export type GeckoAlertData = {
  priority: AlertPriority
  timestamp: string
  acknowledged: boolean
  status: AlertStatus
  alerted_value: number
  setpoint: number
  source_type?: string
  title?: string
  summary?: string
  severity?: string
  chart_id?: string
  site_id?: string
  organization_id?: string
  primary_datapoint_id?: string
  datapoint_ids?: string[]
  /** For `fim-sentinel` alerts, the datapoint the sentinel scored. */
  signal_id?: string
  /** The sentinel that raised this alert (`fim-sentinel` alerts only). */
  sentinel_entry_id?: string
  /** The asset instance the sentinel monitors (`fim-asset-instance-<uid>`). */
  asset_instance_entry_id?: string
  /** The FIM Subject behind that asset instance. */
  subject_uid?: string
  /** Emission-time snapshot of the sentinel run (`fim-sentinel` alerts only).
   * Monitor config is editable, so these — not current config — are the
   * historical truth for this alert. */
  sentinel_target?: string
  sentinel_method?: string
  sentinel_band_k?: number
  sentinel_origin?: string
  sentinel_in_band?: boolean
  residual?: number
  threshold?: number
  magnitude?: number
  render_start_at?: string
  render_end_at?: string
  focus_start_at?: string
  focus_end_at?: string
  case_id?: string
  ai_explanation?: string
  probable_causes?: ProbableCause[]
  monitor_lineage?: MonitorLineage
  /** Subsystem id for constructor-* alerts; written as `data.subsystem` and
   * mirrored to `tags.subsystem` by alert_builder.py. Required for resolving
   * monitor-side artifacts whose `unit` tag matches this value. */
  subsystem?: string
}

const parseMonitorLineage = (input: unknown): MonitorLineage | undefined => {
  if (!input || typeof input !== 'object') return undefined
  const o = input as Record<string, unknown>
  const site_id = parseString(o.site_id)
  const unit_slug = parseString(o.unit_slug)
  const subsystem_model_artifact_id = parseString(o.subsystem_model_artifact_id)
  const generator_version = parseString(o.generator_version)
  if (
    !site_id ||
    !unit_slug ||
    !subsystem_model_artifact_id ||
    !generator_version
  ) {
    return undefined
  }
  return {
    site_id,
    unit_slug,
    subsystem_model_artifact_id,
    generator_version,
    subsystem_model_version: parseNumber(o.subsystem_model_version),
    subsystem_partition_artifact_id: parseString(
      o.subsystem_partition_artifact_id,
    ),
    site_context_artifact_id: parseString(o.site_context_artifact_id),
    generated_module_path: parseString(o.generated_module_path),
  }
}

export class GeckoAlert extends GeckoEntryBase<
  GeckoAlertData,
  {
    organization: string
    site: string
    unit: string
    /** Constructor-side subsystem id; the same notion as `unit` for monitor
     * alerts, but written by alert_builder.py under the key `subsystem`. */
    subsystem: string
    datapoints: string
    /** Asset-instance entry id on `fim-sentinel` alerts. */
    instance: string
  },
  RolodexEntryIndexType
> {
  constructor(json: RolodexEntryJson) {
    const data: GeckoAlertData = {
      priority: (parseNumber(json.data.priority) ?? 2) as AlertPriority,
      timestamp: json.data.timestamp as string,
      acknowledged: parseBoolean(json.data.acknowledged) ?? false,
      status: (parseString(json.data.status) ?? 'Normal') as AlertStatus,
      alerted_value: parseNumber(json.data.alerted_value) ?? 0,
      setpoint: parseNumber(json.data.setpoint) ?? 0,
      source_type: parseString(json.data.source_type),
      title: parseString(json.data.title),
      summary: parseString(json.data.summary),
      severity: parseString(json.data.severity),
      chart_id: parseString(json.data.chart_id),
      site_id: parseString(json.data.site_id),
      organization_id: parseString(json.data.organization_id),
      primary_datapoint_id: parseString(json.data.primary_datapoint_id),
      datapoint_ids: parseArray(json.data.datapoint_ids) as string[] | undefined,
      signal_id: parseString(json.data.signal_id),
      sentinel_entry_id: parseString(json.data.sentinel_entry_id),
      asset_instance_entry_id: parseString(json.data.asset_instance_entry_id),
      subject_uid: parseString(json.data.subject_uid),
      sentinel_target: parseString(json.data.sentinel_target),
      sentinel_method: parseString(json.data.sentinel_method),
      sentinel_band_k: parseFiniteNumber(json.data.sentinel_band_k),
      sentinel_origin: parseString(json.data.sentinel_origin),
      sentinel_in_band: parseBoolean(json.data.sentinel_in_band),
      residual: parseFiniteNumber(json.data.residual),
      threshold: parseFiniteNumber(json.data.threshold),
      magnitude: parseFiniteNumber(json.data.magnitude),
      render_start_at: parseString(json.data.render_start_at),
      render_end_at: parseString(json.data.render_end_at),
      focus_start_at: parseString(json.data.focus_start_at),
      focus_end_at: parseString(json.data.focus_end_at),
      case_id: parseString(json.data.case_id),
      ai_explanation: parseString(json.data.ai_explanation),
      probable_causes: parseArray(json.data.probable_causes) as
        | ProbableCause[]
        | undefined,
      monitor_lineage: parseMonitorLineage(json.data.monitor_lineage),
      subsystem: parseString(json.data.subsystem),
    }

    const rawTags = json.tags as Record<string, unknown>
    const tags = {
      organization: parseString(json.tags.organization) ?? '',
      site: parseString(json.tags.site) ?? '',
      unit: parseString(json.tags.unit) ?? '',
      subsystem: parseString(rawTags.subsystem) ?? '',
      datapoints: parseString(json.tags.datapoints) ?? '',
      instance: parseString(rawTags.instance) ?? '',
    }

    super(json, data, tags, {})
  }
}
